/**
 * ShareModal Component
 * Implements requirements 8.1, 8.3, 8.4:
 * - LINE共有テキスト生成
 * - 共有ボタンUI
 * - 個人情報除外処理
 */

import React, { useState, useEffect } from 'react';
import { Plan, Spot, Location } from '../types/core';
import { MapLinkGenerator } from '../utils/map-links';
import { Button } from './Button';
import { Card } from './Card';

export interface ShareModalProps {
  plan: Plan;
  startLocation: Location;
  isOpen: boolean;
  onClose: () => void;
}

export interface ShareTextOptions {
  includeBudget: boolean;
  includeWalkingTime: boolean;
  includeMapLink: boolean;
  customMessage?: string;
}

const mapLinkGenerator = new MapLinkGenerator();

export const ShareModal: React.FC<ShareModalProps> = ({
  plan,
  startLocation,
  isOpen,
  onClose
}) => {
  const [shareOptions, setShareOptions] = useState<ShareTextOptions>({
    includeBudget: true,
    includeWalkingTime: true,
    includeMapLink: true
  });
  const [shareText, setShareText] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  // Generate share text when options change
  useEffect(() => {
    const text = generateShareText(plan, startLocation, shareOptions);
    setShareText(text);
  }, [plan, startLocation, shareOptions]);

  // Reset copy success state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setCopySuccess(false);
    }
  }, [isOpen]);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleLineShare = () => {
    const lineUrl = `https://social-plugins.line.me/lineit/share?text=${encodeURIComponent(shareText)}`;
    window.open(lineUrl, '_blank', 'noopener,noreferrer');
  };

  const handleTwitterShare = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    window.open(twitterUrl, '_blank', 'noopener,noreferrer');
  };

  const handleFacebookShare = () => {
    const mapLinks = mapLinkGenerator.generatePlanMapLinks(plan, startLocation);
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(mapLinks.google)}&quote=${encodeURIComponent(shareText)}`;
    window.open(facebookUrl, '_blank', 'noopener,noreferrer');
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent('外出プランの共有');
    const body = encodeURIComponent(shareText);
    const emailUrl = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = emailUrl;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">プランを共有</h3>
            <Button
              onClick={onClose}
              variant="outline"
              className="text-gray-400 hover:text-gray-600 border-none shadow-none"
              aria-label="閉じる"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>

          {/* Share Options */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">共有内容の設定</h4>
            
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={shareOptions.includeBudget}
                  onChange={(e) => setShareOptions(prev => ({ ...prev, includeBudget: e.target.checked }))}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">予算を含める</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={shareOptions.includeWalkingTime}
                  onChange={(e) => setShareOptions(prev => ({ ...prev, includeWalkingTime: e.target.checked }))}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">徒歩時間を含める</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={shareOptions.includeMapLink}
                  onChange={(e) => setShareOptions(prev => ({ ...prev, includeMapLink: e.target.checked }))}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">地図リンクを含める</span>
              </label>
            </div>

            {/* Custom Message */}
            <div>
              <label htmlFor="customMessage" className="block text-sm font-medium text-gray-700 mb-1">
                カスタムメッセージ（任意）
              </label>
              <textarea
                id="customMessage"
                value={shareOptions.customMessage || ''}
                onChange={(e) => setShareOptions(prev => ({ ...prev, customMessage: e.target.value }))}
                placeholder="一緒に行きませんか？"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={2}
              />
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">プレビュー</h4>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                {shareText}
              </pre>
            </div>
            
            <div className="flex space-x-2">
              <Button
                onClick={handleCopyToClipboard}
                variant="outline"
                className="flex-1"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                {copySuccess ? 'コピー完了!' : 'コピー'}
              </Button>
              
              <span className="text-xs text-gray-500 self-center">
                {shareText.length} 文字
              </span>
            </div>
          </div>

          {/* Share Buttons */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">共有先を選択</h4>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={handleLineShare}
                variant="outline"
                className="flex items-center justify-center space-x-2 bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
                </svg>
                <span>LINE</span>
              </Button>
              
              <Button
                onClick={handleTwitterShare}
                variant="outline"
                className="flex items-center justify-center space-x-2 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
                <span>Twitter</span>
              </Button>
              
              <Button
                onClick={handleFacebookShare}
                variant="outline"
                className="flex items-center justify-center space-x-2 bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span>Facebook</span>
              </Button>
              
              <Button
                onClick={handleEmailShare}
                variant="outline"
                className="flex items-center justify-center space-x-2 bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>メール</span>
              </Button>
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex">
              <svg className="w-5 h-5 text-yellow-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-sm">
                <p className="text-yellow-800 font-medium">プライバシーについて</p>
                <p className="text-yellow-700 mt-1">
                  共有テキストには個人を特定できる情報（正確な位置情報など）は含まれません。
                  一般的なエリア名とスポット情報のみが共有されます。
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

/**
 * Generate share text for a plan
 * Implements requirement 8.3: 個人情報除外処理
 */
function generateShareText(
  plan: Plan, 
  startLocation: Location, 
  options: ShareTextOptions
): string {
  const spots = plan.steps.filter(step => step.type === 'spot') as Spot[];
  const mapLinks = mapLinkGenerator.generatePlanMapLinks(plan, startLocation);
  
  let text = '';
  
  // Custom message
  if (options.customMessage) {
    text += `${options.customMessage}\n\n`;
  }
  
  // Plan title
  text += `🚶‍♀️ ${Math.floor(plan.estimatedDuration)}分のお出かけプラン\n\n`;
  
  // Spots (without exact addresses for privacy)
  spots.forEach((spot, index) => {
    text += `${index + 1}. ${spot.name}`;
    
    // Add genre
    text += ` (${getGenreDisplayName(spot.genre)})`;
    
    // Add duration
    text += ` - ${spot.duration}分`;
    
    // Add budget if enabled
    if (options.includeBudget && spot.budget) {
      text += ` ¥${spot.budget.min.toLocaleString()}-${spot.budget.max.toLocaleString()}`;
    }
    
    text += '\n';
  });
  
  text += '\n';
  
  // Walking time if enabled
  if (options.includeWalkingTime) {
    text += `🚶 徒歩時間: ${plan.walkingTime}分\n`;
  }
  
  // Budget summary if enabled
  if (options.includeBudget) {
    text += `💰 予算: ¥${plan.budget.min.toLocaleString()}-${plan.budget.max.toLocaleString()}\n`;
  }
  
  // Map link if enabled
  if (options.includeMapLink && mapLinks.google) {
    text += `\n📍 地図で見る: ${mapLinks.google}`;
  }
  
  text += '\n\n#外出プラン #マイクロ外出';
  
  return text;
}

function getGenreDisplayName(genre: string): string {
  const names: Record<string, string> = {
    cafe: 'カフェ',
    bookstore: '書店',
    park: '公園',
    museum: '美術館',
    sweets: 'スイーツ',
    event: 'イベント',
    bar: 'バー',
    restaurant: 'レストラン'
  };
  
  return names[genre] || genre;
}